import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRecentArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              文章未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或地址有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = getRecentArticles(4).filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.publishDate,
          author: { '@type': 'Person', name: article.author },
          publisher: {
            '@type': 'Organization',
            name: '性福宝导航',
          },
        }}
      />
      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '文章', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {category && (
                <span className="text-xs text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded">
                  {category.name}
                </span>
              )}
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
              <span className="text-xs text-foreground-400">{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>

          <div
            className="prose prose-sm max-w-none mb-10 text-foreground-800 leading-relaxed
              prose-h2:text-lg prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-700
              prose-ul:text-sm prose-ul:leading-relaxed prose-ul:text-foreground-700
              prose-li:mb-2 prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-8">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1.5">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}